import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook to block navigation when there are unsaved changes or active operations
 * WITHOUT using useBlocker (for compatibility with BrowserRouter)
 * @param {Object} options - Configuration options
 * @param {boolean} options.recordingParent - Whether recording is active
 * @param {string} options.notes - Manual notes content
 * @param {boolean} options.generatingNotes - Whether AI notes are being generated
 * @param {boolean} options.uploading - Whether transcript is being uploaded
 * @param {boolean} options.pollingNote - Whether AI note is being polled
 * @param {boolean} options.fetchingNote - Whether AI note is being fetched
 * @param {boolean} options.isNoteSigned - Whether note is signed
 * @param {boolean} options.noteLocked - Whether note is locked
 * @param {Function} options.onBeforeLeave - Callback to execute before leaving (auto-save, cleanup)
 * @returns {Object} - Navigation blocker state and handlers
 */
export const useNavigationBlocker = ({
  recordingParent = false,
  notes = '',
  generatingNotes = false,
  uploading = false,
  pollingNote = false,
  fetchingNote = false,
  isNoteSigned = false,
  noteLocked = false,
  onBeforeLeave = null,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isBlockingRef = useRef(false);

  // Determine if navigation should be blocked
  const shouldBlock = 
    recordingParent ||           // Recording active
    uploading ||                 // Upload in progress
    (generatingNotes && !isNoteSigned && !noteLocked);  // AI generating (unless locked)

  // Update ref when shouldBlock changes
  useEffect(() => {
    isBlockingRef.current = shouldBlock;
  }, [shouldBlock]);

  // Get the primary blocking reason for display
  const getBlockingReason = useCallback(() => {
    if (recordingParent) return 'recording';
    if (uploading) return 'uploading';
    if (generatingNotes && !isNoteSigned && !noteLocked) return 'generating';
    return null;
  }, [recordingParent, uploading, generatingNotes, isNoteSigned, noteLocked]);

  // Handle browser beforeunload event (close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (shouldBlock && !isLeaving) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // For older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, isLeaving]);

  // Intercept Link clicks and back button
  useEffect(() => {
    const handleClick = (e) => {
      // Check if it's a navigation link
      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      // Only intercept internal navigation
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      // Check if we should block
      if (isBlockingRef.current && !isLeaving) {
        e.preventDefault();
        e.stopPropagation();
        setPendingNavigation({ path: href, type: 'link' });
        setShowConfirmation(true);
      }
    };

    // Add click listener to document
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [isLeaving]);

  // Handle browser back/forward buttons
  useEffect(() => {
    if (!shouldBlock) return;

    const handlePopState = (e) => {
      if (isBlockingRef.current && !isLeaving) {
        // Push current state back to prevent navigation
        window.history.pushState(null, '', location.pathname + location.search);
        setPendingNavigation({ path: null, type: 'popstate' });
        setShowConfirmation(true);
      }
    };

    // Push a dummy state to detect back button
    window.history.pushState(null, '', location.pathname + location.search);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlock, location, isLeaving]);

  // Handle user confirming they want to leave
  const handleConfirmLeave = useCallback(async () => {
    setIsLeaving(true);
    
    try {
      // Execute cleanup/auto-save callback
      if (onBeforeLeave) {
        await onBeforeLeave();
      }
    } catch (error) {
      console.error('Error during navigation cleanup:', error);
    }

    setShowConfirmation(false);
    
    // Proceed with navigation
    if (pendingNavigation) {
      if (pendingNavigation.type === 'link' && pendingNavigation.path) {
        // Navigate to the pending path
        setTimeout(() => {
          navigate(pendingNavigation.path);
          setIsLeaving(false);
        }, 100);
      } else if (pendingNavigation.type === 'popstate') {
        // Go back
        setTimeout(() => {
          window.history.back();
          setIsLeaving(false);
        }, 100);
      }
    }
    
    setPendingNavigation(null);
  }, [onBeforeLeave, pendingNavigation, navigate]);

  // Handle user canceling navigation
  const handleCancelLeave = useCallback(() => {
    setShowConfirmation(false);
    setPendingNavigation(null);
  }, []);

  return {
    showConfirmation,
    blockingReason: getBlockingReason(),
    shouldBlock,
    handleConfirmLeave,
    handleCancelLeave,
    isLeaving,
  };
};
